import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import baseUrl from "../api/utils";

const StockReportView = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchStockReport = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${baseUrl}/reports/stock`, {
          headers: { Authorization: token },
        });
        setData(res.data || []);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate("/unauthorized");
          return;
        }
        console.error("Failed to load stock report", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStockReport();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(
      (row) =>
        row.itemName?.toLowerCase().includes(q) ||
        row.itemCode?.toLowerCase().includes(q) ||
        row.categoryName?.toLowerCase().includes(q)
    );
  }, [data, search]);

  const lowStockCount = data.filter((r) => r.isLowStock).length;
  const totalStockValue = data.reduce((s, r) => s + (r.stockValue || 0), 0);

  // ── CSV Export ──
  const handleExportCsv = () => {
    const headers = ["Item Name", "Code", "Category", "Unit", "Current Stock", "Min Stock", "Status", "Stock Value", "Location"];
    const rows = filtered.map((r) => [
      r.itemName || "",
      r.itemCode || "",
      r.categoryName || "",
      r.unitName || "",
      r.currentStock ?? 0,
      r.minStockQty ?? 0,
      r.isLowStock ? "LOW STOCK" : "OK",
      (r.stockValue || 0).toFixed(2),
      r.location || "",
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `stock_report_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const cellStyle = { border: "1px solid #eee", padding: "8px 10px", fontSize: "13px" };
  const thStyle = { ...cellStyle, background: "#f5f6fa", fontWeight: "600", textAlign: "left", color: "#555" };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>Loading stock report...</div>;
  }

  return (
    <div>
      {/* ── Summary cards ── */}
      <div style={{ display: "flex", gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: "160px", background: "#f0f7ff", borderRadius: "8px", padding: "14px 16px" }}>
          <div style={{ fontSize: "12px", color: "#888" }}>Total Items</div>
          <div style={{ fontSize: "20px", fontWeight: "700", color: "#222" }}>{data.length}</div>
        </div>
        <div style={{ flex: 1, minWidth: "160px", background: lowStockCount > 0 ? "#fff0f0" : "#f0fdf4", borderRadius: "8px", padding: "14px 16px" }}>
          <div style={{ fontSize: "12px", color: "#888" }}>Low Stock Items</div>
          <div style={{ fontSize: "20px", fontWeight: "700", color: lowStockCount > 0 ? "#e74c3c" : "#27ae60" }}>{lowStockCount}</div>
        </div>
        <div style={{ flex: 1, minWidth: "160px", background: "#f5f5f5", borderRadius: "8px", padding: "14px 16px" }}>
          <div style={{ fontSize: "12px", color: "#888" }}>Total Stock Value</div>
          <div style={{ fontSize: "20px", fontWeight: "700", color: "#222" }}>₹{totalStockValue.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
        </div>
      </div>

      {/* ── Search + Export ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", gap: "12px" }}>
        <div style={{ position: "relative", flex: 1, maxWidth: "320px" }}>
          <input
            type="text"
            placeholder="Search item, code or category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%", padding: "8px 32px 8px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", outline: "none", boxSizing: "border-box" }}
          />
          <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#888", fontSize: "12px" }} />
        </div>
        <button
          className="btn btn-danger btn-sm"
          onClick={handleExportCsv}
          disabled={filtered.length === 0}
        >
          <i className="fa-solid fa-download mr-1"></i> Export CSV
        </button>
      </div>
{/* ── Table ── */}
<div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "480px", border: "1px solid #eee", borderRadius: "6px" }}>
  <table style={{ width: "100%", borderCollapse: "collapse" }}>
    <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
      <tr>
        <th style={thStyle}>Item Name</th>
        <th style={thStyle}>Code</th>
        <th style={thStyle}>Category</th>
        <th style={thStyle}>Unit</th>
        <th style={{ ...thStyle, textAlign: "right" }}>Stock</th>
        <th style={{ ...thStyle, textAlign: "right" }}>Min Qty</th>
        <th style={{ ...thStyle, textAlign: "center" }}>Status</th>
        <th style={{ ...thStyle, textAlign: "right" }}>Stock Value</th>
        <th style={thStyle}>Location</th>
      </tr>
    </thead>

          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ ...cellStyle, textAlign: "center", color: "#aaa", padding: "30px" }}>
                  No items found.
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr key={row.itemId} style={{ background: row.isLowStock ? "#fff8f8" : "#fff" }}>
                  <td style={{ ...cellStyle, fontWeight: "500" }}>{row.itemName}</td>
                  <td style={cellStyle}>{row.itemCode || "—"}</td>
                  <td style={cellStyle}>{row.categoryName || "—"}</td>
                  <td style={cellStyle}>{row.unitName || "—"}</td>
                  <td style={{ ...cellStyle, textAlign: "right", fontWeight: row.isLowStock ? "700" : "400", color: row.isLowStock ? "#e74c3c" : "#333" }}>
                    {row.currentStock}
                  </td>
                  <td style={{ ...cellStyle, textAlign: "right", color: "#888" }}>{row.minStockQty}</td>
                  <td style={{ ...cellStyle, textAlign: "center" }}>
                    {row.isLowStock ? (
                      <span style={{ background: "#fee", color: "#e74c3c", padding: "2px 10px", borderRadius: "10px", fontSize: "11px", fontWeight: "700" }}>
                        LOW STOCK
                      </span>
                    ) : (
                      <span style={{ background: "#eafaf0", color: "#27ae60", padding: "2px 10px", borderRadius: "10px", fontSize: "11px", fontWeight: "700" }}>
                        OK
                      </span>
                    )}
                  </td>
                  <td style={{ ...cellStyle, textAlign: "right" }}>
                    ₹{(row.stockValue || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </td>
                  <td style={cellStyle}>{row.location || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StockReportView;